import { supabase } from '@/integrations/supabase/client';
import { SavedQuery, CreateSavedQueryRequest, UpdateSavedQueryRequest } from '@/types/savedQuery';

// Helper function to get current user's organization
const getCurrentOrganization = async () => {
	const { data: { user } } = await supabase.auth.getUser();
	if (!user) throw new Error('User not authenticated');

	const { data: userOrgs } = await supabase
		.from('user_organization')
		.select('organization_uuid')
		.eq('user_uuid', user.id)
		.single();

	if (!userOrgs) throw new Error('User not associated with any organization');
	return userOrgs.organization_uuid;
};

// Helper function to get current user
const getCurrentUser = async () => {
	const { data: { user } } = await supabase.auth.getUser();
	if (!user) throw new Error('User not authenticated');
	return user.id;
};

export const savedQueryService = {
	// Get all saved queries accessible to the current user
	async getSavedQueries(): Promise<SavedQuery[]> {
		const organizationUuid = await getCurrentOrganization();
		const userId = await getCurrentUser();

		// @ts-ignore - saved_queries table exists but types not yet regenerated
		const { data, error } = await supabase
			.from('saved_queries')
			.select('*')
			.eq('organization_uuid', organizationUuid)
			.or(`created_by.eq.${userId},is_public.eq.true,shared_with_user_ids.cs.{${userId}}`)
			.order('created_at', { ascending: false });

		if (error) throw error;
		return data || [];
	},

	// Get a specific saved query by ID
	async getSavedQuery(id: string): Promise<SavedQuery> {
		// @ts-ignore - saved_queries table exists but types not yet regenerated
		const { data, error } = await supabase
			.from('saved_queries')
			.select('*')
			.eq('id', id)
			.single();

		if (error) throw error;
		return data;
	},

	// Create a new saved query
	async createSavedQuery(request: CreateSavedQueryRequest): Promise<SavedQuery> {
		const organizationUuid = await getCurrentOrganization();
		const userId = await getCurrentUser();

		// @ts-ignore - saved_queries table exists but types not yet regenerated
		const { data, error } = await supabase
			.from('saved_queries')
			.insert({
				name: request.name,
				description: request.description,
				query_definition: request.query_definition,
				is_public: request.is_public ?? false,
				shared_with_user_ids: request.shared_with_user_ids ?? [],
				created_by: userId,
				organization_uuid: organizationUuid
			})
			.select('*')
			.single();

		if (error) throw error;
		return data;
	},

	// Update a saved query
	async updateSavedQuery(id: string, updates: UpdateSavedQueryRequest): Promise<SavedQuery> {
		// @ts-ignore - saved_queries table exists but types not yet regenerated
		const { data, error } = await supabase
			.from('saved_queries')
			.update({
				...updates,
				updated_at: new Date().toISOString()
			})
			.eq('id', id)
			.select('*')
			.single();

		if (error) throw error;
		return data;
	},

	// Delete a saved query
	async deleteSavedQuery(id: string): Promise<void> {
		// @ts-ignore - saved_queries table exists but types not yet regenerated
		const { error } = await supabase
			.from('saved_queries')
			.delete()
			.eq('id', id);

		if (error) throw error;
	},

	// Track query execution (increment count and update timestamp)
	async trackExecution(id: string): Promise<void> {
		// Get current count
		// @ts-ignore - saved_queries table exists but types not yet regenerated
		const { data: current } = await supabase
			.from('saved_queries')
			.select('execution_count')
			.eq('id', id)
			.single();

		// @ts-ignore - saved_queries table exists but types not yet regenerated
		await supabase
			.from('saved_queries')
			.update({
				execution_count: (current?.execution_count || 0) + 1,
				last_executed_at: new Date().toISOString()
			})
			.eq('id', id);
	}
};
